import React from "react";
import styled from "styled-components";
import { Flex } from "../../../../../Flex";
import { Span } from "../../../../../Span";
import { DisconnectionPropertyDetails } from "../../../../../../types/DisconnectionPropertyDetails";
import { ReviewFormPropertyDetailsMeter } from "../ReviewFormPropertyDetailsMeter";
import { Box } from "../../../../../Box";
import { getAddressInformation } from "../../../../../LiveningIcpForm/_/LiveningIcpReviewForm/_/FormattedAddressUtil/FormattedAddressUtil";
import { mapAccessIssues } from "../../../DisconnectionFormUtils/DisconnectionFormUtils";

interface Props {
  id: string;
  values: DisconnectionPropertyDetails;
}

const MetersWrapper = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.neutrals.ln10};
  border: 1px solid;
  border-color: ${({ theme }) => theme.colors.neutrals.ln30};
`;

const LabelWrapper = styled(Span)`
  color: ${({ theme }) => theme.colors.neutrals.dn40};
  min-width: 0;
  flex-basis: 0;
`;

const ValueWrapper = styled(Span)`
  min-width: 0;
  flex-basis: 0;
`;

export const ReviewFormPropertyDetails: React.FC<Props> = ({ id, values }) => {
  return (
    <Box id={id}>
      <Flex flexDirection="row" mb={3}>
        <LabelWrapper lineHeight={2} flexGrow={[0.5, 0.5, 0.5, 0.4, 0.35]}>
          Installation Control Point (ICP)
        </LabelWrapper>

        <ValueWrapper
          data-testid={`${id}-icp`}
          lineHeight={2}
          ml={3}
          flexGrow={[0.5, 0.5, 0.5, 0.6, 0.65]}
        >
          {values.icpAddressLookupEnabled
            ? values.icpAddressLookup.id
            : "Not provided"}
        </ValueWrapper>
      </Flex>

      {values.icpAddressLookupEnabled && (
        <Flex flexDirection="row" mb={3}>
          <LabelWrapper lineHeight={2} flexGrow={[0.5, 0.5, 0.5, 0.4, 0.35]}>
            Address
          </LabelWrapper>

          <ValueWrapper
            data-testid={`${id}-icp-address`}
            lineHeight={2}
            ml={3}
            flexGrow={[0.5, 0.5, 0.5, 0.6, 0.65]}
          >
            {values.icpAddressLookup.label}
          </ValueWrapper>
        </Flex>
      )}

      <Flex flexDirection="row" mb={3}>
        <LabelWrapper lineHeight={2} flexGrow={[0.5, 0.5, 0.5, 0.4, 0.35]}>
          Number of electricity meters to be disconnected
        </LabelWrapper>

        <ValueWrapper
          data-testid={`${id}-meter-count`}
          lineHeight={2}
          ml={3}
          flexGrow={[0.5, 0.5, 0.5, 0.6, 0.65]}
        >
          {values.meterInfo.meterCount}
        </ValueWrapper>
      </Flex>

      {values.meterInfo.meterCount > 0 && (
        <MetersWrapper
          flexDirection="column"
          borderRadius="radio"
          padding={[3, 3, 3, 4]}
          mb={3}
        >
          {values.meterInfo.meters.map((meter, index) => {
            return (
              <ReviewFormPropertyDetailsMeter
                key={`${id}-meter-${index}`}
                idPrefix={`${id}-meter`}
                index={index}
                meter={meter}
              />
            );
          })}
          {!values.icpAddressLookupEnabled && (
            <Flex flexDirection="row" mt={3}>
              <LabelWrapper
                lineHeight={2}
                flexGrow={[0.5, 0.5, 0.5, 0.4, 0.35]}
              >
                Address
              </LabelWrapper>

              <ValueWrapper
                data-testid={`${id}-meter-address`}
                lineHeight={2}
                ml={3}
                flexGrow={[0.5, 0.5, 0.5, 0.6, 0.65]}
              >
                {getAddressInformation(values.meterInfo.addressInformation)}
              </ValueWrapper>
            </Flex>
          )}
        </MetersWrapper>
      )}

      <Flex flexDirection="row" mb={3}>
        <LabelWrapper lineHeight={2} flexGrow={[0.5, 0.5, 0.5, 0.4, 0.35]}>
          Property type
        </LabelWrapper>

        <ValueWrapper
          data-testid={`${id}-property-type`}
          lineHeight={2}
          ml={3}
          flexGrow={[0.5, 0.5, 0.5, 0.6, 0.65]}
        >
          {values.type}
        </ValueWrapper>
      </Flex>

      <Flex flexDirection="row" mb={3}>
        <LabelWrapper lineHeight={2} flexGrow={[0.5, 0.5, 0.5, 0.4, 0.35]}>
          Access issues
        </LabelWrapper>

        <ValueWrapper
          data-testid={`${id}-access-issues`}
          lineHeight={2}
          ml={3}
          flexGrow={[0.5, 0.5, 0.5, 0.6, 0.65]}
        >
          {mapAccessIssues(values)}
        </ValueWrapper>
      </Flex>
    </Box>
  );
};
